import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { useStyles } from "../../../theme/appStyles";
import { useNavigate } from "react-router";
import {
  useGetAllByPolicyNumberQuery,
  useGetCustomerRelationQuery,
  useGetCustomerQuery,
  useGetFinalSummaryReportQuery,
  useGetProductBrandQuery,
  useGetProductQuery,
  useGetSettingQuery,
  useGetMultiCustomerSummaryReportQuery,
} from "../../../Api/attoDeskApi";
import {
  ICustomer,
  IFinalSummaryReport,
  IPolicy,
  IProductBrand,
  IProductPopUP,
  ICustomerRelative2,
  ICompanyDetails,
} from "../../../Api/Interface/api.interface";
import dayjs, { Dayjs } from "dayjs";
import { useParams } from "react-router-dom";
import ReactToPrint from "react-to-print";
import LocalPrintshopSharpIcon from "@mui/icons-material/LocalPrintshopSharp";
import LogoMain from "../../../Images/logo.png";
import { formatPhoneNumberForDisplay } from "../../../Components/Validation/validationUtils";
import { toProperCase } from "../../../Components/Validation/toProperCase";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { styled } from "@mui/material/styles";
import { formatNumber } from "../../../Components/formatNumber/formatNumber";
import { Link } from "react-router-dom";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { poppinsRegular, poppinsBold } from "./poppins-fonts";

interface MultiPDFProps {
  selectedRowIds: (string | number)[];
}

const capitalizeFirstLetter = (name: string | null | undefined) => {
  if (!name) return "";
  return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
};

const toAllCaps = (text: string | null | undefined): string => {
  if (!text) return "";
  return text.toUpperCase();
};

const MultiPDFDownload: React.FC<MultiPDFProps> = ({ selectedRowIds }) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const theme = useTheme();
  const [initialData, setInitialData] = useState<ICompanyDetails | null>(null);

  const { data, isLoading, isError } = useGetMultiCustomerSummaryReportQuery({
    ids: selectedRowIds || [],
  });

  const {
    data: settingData,
    isLoading: settingIsLoading,
    isError: settingIsError,
  } = useGetSettingQuery({
    searchText: "",
    id: "1",
  });

  const {
    data: customerProjectData,
    isLoading: customerProjectDataIsLoading,
    refetch: cutomerProjectRefetch,
    isError: cutomerProjectisError,
  } = useGetAllByPolicyNumberQuery({
    searchText: "",
    id: "",
    policyCustomerId: "",
    policyNumber: "",
  });

  const {
    data: summaryReportData,
    isLoading: summaryReportIsLoading,
    isError: SummaryReportIsError,
  } = useGetFinalSummaryReportQuery({
    id: "",
  });

  const { data: brandListData, isLoading: brandIsLoading } =
    useGetProductBrandQuery({
      searchText: "",
      isActive: true,
    });

  const { data: customerFullData, isLoading: customerFullDataISLoading } =
    useGetCustomerQuery({
      searchText: "",
      id: "",
    });

  const {
    data: productData,
    isLoading: productDataIsLoading,
    isError: productDataError,
  } = useGetProductQuery({
    searchText: "",
    id: "",
    productBrandId: null,
    productCategoryId: null,
    productTagIds: null,
    productViewOnline: null,
    createdDateStart: null,
    createdDateEnd: null,
    createdDate: null,
  });

  const policyCustomerId = String("");

  const {
    data: relativeData,
    isLoading: relativeIsLoading,
    isError: relativeError,
  } = useGetCustomerRelationQuery(
    { policyCustomerId },
    { skip: !policyCustomerId }
  );

  useEffect(() => {
    if (settingData?.data) {
      setInitialData(settingData.data as ICompanyDetails);
    }
  }, [settingData]);

  const customer = useMemo(() => {
    if (data && Array.isArray(data.data) && data.data.length > 0) {
      return data.data[0] as ICustomer;
    }
    return null;
  }, [data]);

  const customerListReport = useMemo(() => {
    return data?.data as ICustomer[];
  }, [data?.data]);

  const project: IPolicy[] = useMemo(() => {
    if (customerProjectData && Array.isArray(customerProjectData.data)) {
      return customerProjectData.data as IPolicy[];
    }
    return [];
  }, [customerProjectData]);

  const brandList = useMemo(() => {
    return brandListData?.data as IProductBrand[];
  }, [brandListData?.data]);

  const customerAllList = useMemo(() => {
    return customerFullData?.data as ICustomer[];
  }, [customerFullData?.data]);

  const productList = useMemo(() => {
    return productData?.data as IProductPopUP[];
  }, [productData?.data]);

  const finalSummaryList = useMemo(() => {
    return summaryReportData?.data as IFinalSummaryReport;
  }, [summaryReportData?.data]);

  const relationDataList = useMemo(() => {
    return (relativeData?.data ?? []) as ICustomerRelative2[];
  }, [relativeData?.data]);

  const brandMap = useMemo(() => {
    const map = new Map();
    brandList?.forEach((category) => {
      map.set(category.id, category.productBrandName);
    });
    return map;
  }, [brandList]);

  const productMap = useMemo(() => {
    const map = new Map();
    productList?.forEach((brand) => {
      map.set(brand.id, brand.productName);
    });
    return map;
  }, [productList]);

  const relativeMap = useMemo(() => {
    const map = new Map();
    relationDataList?.forEach((item) => {
      map.set(item.id, `${toProperCase(item?.related_customer_name)}`);
    });
    return map;
  }, [relationDataList]);

  const customerMap = useMemo(() => {
    const map = new Map();
    customerAllList?.forEach((category) => {
      map.set(
        category.id,
        capitalizeFirstLetter(category.customerFirstName) +
          " " +
          capitalizeFirstLetter(category.customerLastName)
      );
    });
    return map;
  }, [customerAllList]);

  const today = dayjs();
  const formattedDate = dayjs(today).format("MMM D, YYYY");

  const formatAddress = (
    customerAddresLine: string,
    customerCity: string,
    customerProvince: string,
    customerPostalCode: string
  ) => {
    return `${capitalizeFirstLetter(
      customerAddresLine
    )} ${capitalizeFirstLetter(customerCity)},\n${capitalizeFirstLetter(
      customerProvince
    )} ${capitalizeFirstLetter(customerPostalCode)}`;
  };

  const AddressOrder = formatAddress(
    customer?.customerAddresLine || "",
    customer?.customerCity || "",
    customer?.customerProvince || "",
    customer?.customerPostalCode || ""
  );

  const fontwhighte = 550;
  const documentName = `${customer?.customerFirstName || ""} ${
    customer?.customerLastName || ""
  }_${new Date().toLocaleDateString()}`;

  // const generatePDFForCustomer = async (customer: ICustomer) => {
  //   const element = document.getElementById(`pdf-customer-${customer.id}`);
  //   if (!element) return;

  //   // Capture the element as a canvas
  //   const canvas = await html2canvas(element, {
  //     scale: 2, // better quality
  //     useCORS: true, // if images are from external sources
  //     scrollY: -window.scrollY,
  //   });

  //   const imgData = canvas.toDataURL("image/png");

  //   const pdf = new jsPDF("p", "mm", "a4");
  //   const pdfWidth = pdf.internal.pageSize.getWidth();
  //   const pdfHeight = pdf.internal.pageSize.getHeight();

  //   const imgWidth = pdfWidth;
  //   const imgHeight = (canvas.height * imgWidth) / canvas.width;

  //   let heightLeft = imgHeight;
  //   let position = 0;
  //   let pageNumber = 1;
  //   const totalPages = Math.ceil(imgHeight / pdfHeight);

  //   // Add first page
  //   pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
  //   pdf.setFontSize(10);
  //   pdf.text(
  //     `Page ${pageNumber} of ${totalPages}`,
  //     pdfWidth - 30,
  //     pdfHeight - 10
  //   );

  //   heightLeft -= pdfHeight;

  //   // Add remaining pages if content overflows
  //   while (heightLeft > 0) {
  //     position = heightLeft - imgHeight;
  //     pdf.addPage();
  //     pageNumber++;
  //     pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
  //     pdf.setFontSize(10);
  //     pdf.text(
  //       `Page ${pageNumber} of ${totalPages}`,
  //       pdfWidth - 30,
  //       pdfHeight - 10
  //     );
  //     heightLeft -= pdfHeight;
  //   }

  //   pdf.save(`customer_${customer.id}_report.pdf`);
  // };

  const generatePDFForCustomerer = async (customer: ICustomer) => {
    const element = document.getElementById(`pdf-customer-${customer.id}`);
    if (!element) return;

    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      scrollY: -window.scrollY,
    });

    const imgData = canvas.toDataURL("image/png");

    const pdf = new jsPDF("p", "mm", "a4");
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();

    // Margins
    const marginTop = 10;
    const marginBottom = 15;
    const usableHeight = pdfHeight - marginTop - marginBottom;

    const imgWidth = pdfWidth - 10; // small side margin
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    let heightLeft = imgHeight;
    let position = marginTop;
    let pageNumber = 1;
    const totalPages = Math.ceil(imgHeight / usableHeight);

    // Add first page
    pdf.addImage(imgData, "PNG", 5, position, imgWidth, imgHeight);
    pdf.setFontSize(10);
    pdf.text(
      `Page ${pageNumber} of ${totalPages}`,
      pdfWidth - 30,
      pdfHeight - 5
    );

    heightLeft -= usableHeight;

    // Add more pages
    while (heightLeft > 0) {
      position = heightLeft - imgHeight + marginTop;
      pdf.addPage();
      pageNumber++;
      pdf.addImage(imgData, "PNG", 5, position, imgWidth, imgHeight);
      pdf.setFontSize(10);
      pdf.text(
        `Page ${pageNumber} of ${totalPages}`,
        pdfWidth - 30,
        pdfHeight - 5
      );
      heightLeft -= usableHeight;
    }

    pdf.save(`customer_${customer.id}_report.pdf`);
  };

  const generatePDFForCustomer = (customer: ICustomer) => {
    const pdf = new jsPDF("p", "mm", "a4");
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 10;

    // Header: Customer Info
    // pdf.addFont('Helvetica', 'Helvetica', 'normal')
    // pdf.setFont('Helvetica')
    // pdf.setFontSize(11);

    // Register custom Poppins fonts
    pdf.addFileToVFS("Poppins-Regular.ttf", poppinsRegular);
    pdf.addFont("Poppins-Regular.ttf", "Poppins", "normal");
    pdf.addFileToVFS("Poppins-Bold.ttf", poppinsBold);
    pdf.addFont("Poppins-Bold.ttf", "Poppins", "bold");

    // Use it
    pdf.setFont("Poppins", "normal");
    pdf.setFontSize(12);
    // pdf.text("Hello, this is Poppins font!", 20, 20);
    pdf.setFont("Poppins", "normal");

    pdf.text(
      `Name: ${customer.customerFirstName || ""} ${
        customer.customerLastName || ""
      }`,
      margin,
      15
    );
    pdf.text(`Home Phone: ${customer.customerHomeMobileNum || ""}`, margin, 22);
    pdf.text(`Cell Phone: ${customer.customerMobileNum || ""}`, margin, 29);
    pdf.text(`Work Phone: ${customer.customerWorkMobileNum || ""}`, margin, 36);
    pdf.text(`Email: ${customer.customerEmail || ""}`, margin, 43);

    let startY = 50;

    // Loop through policies by grouped policy numbers
    const policies = customer.groupedPolicies as Record<string, IPolicy[]>;
    (customer.policySummaryIds || []).forEach((policyNumber: string) => {
      const records = policies[policyNumber] || [];
      if (records.length === 0) return;

      const firstPolicy = records[0];

      // Add policy header
      pdf.setFontSize(11);
      pdf.setFont("Arial", "bold");
      pdf.text(`Policy Number: ${firstPolicy.policyNumber}`, margin, startY);
      pdf.text(
        `Company: ${
          brandMap.get(Number(firstPolicy.policyInsuranceCompanyId)) || ""
        }`,
        pageWidth / 2,
        startY
      );
      startY += 7;
      pdf.setFont("Arial", "normal");
      pdf.text(
        `Issue Date: ${
          firstPolicy.policyIssueDate
            ? dayjs(firstPolicy.policyIssueDate).format("MMM D, YYYY")
            : "-"
        }`,
        margin,
        startY
      );
      pdf.text(
        `Payor: ${firstPolicy.policyPayor || ""}`,
        pageWidth / 2,
        startY
      );
      startY += 10;

      // Table columns
      const tableColumns = ["Details", "Coverage Amount", "Premium"];
      const tableRows = records.map((policy: IPolicy) => {
        const insuredNames =
          policy.policyInsuredDetails
            ?.map((id) => relativeMap.get(Number(id)) || "")
            .join(", ") || "";

        const beneficiaries =
          policy.policyPrimaryBenificariesNew
            ?.map(
              (b) =>
                `${relativeMap.get(Number(b.column1)) || ""}${
                  b.payoutPercentage ? ` - ${b.payoutPercentage}%` : ""
                }`
            )
            .join(", ") || "";

        return [
          `${
            productMap.get(Number(policy.policyProductId)) || ""
          }\nInsured: ${insuredNames}\nBeneficiaries: ${beneficiaries}\nExpires: ${
            policy.policyIsLifeTimeExpiryDate
              ? "Death"
              : policy.policyExpiryDate
              ? dayjs(policy.policyExpiryDate).format("MMM D, YYYY")
              : "-"
          }`,
          policy.policyCoverageAmount || "0.00",
          policy.policyPremiumDetails || "0.00",
        ];
      });

      (pdf as any).autoTable({
        startY,
        head: [tableColumns],
        body: tableRows,
        theme: "grid",
        headStyles: {
          fillColor: [0, 0, 0],
          textColor: [255, 255, 255],
          fontSize: 11,
        },
        bodyStyles: { fontSize: 11 },
        margin: { left: margin, right: margin },
        didDrawPage: (data: any) => {
          const pageNumber = pdf.getNumberOfPages();
          pdf.setFontSize(10);
          pdf.text(`Page ${pageNumber}`, pageWidth / 2, pageHeight - 10, {
            align: "center",
          });
        },
      });

      startY = (pdf as any).lastAutoTable.finalY + 10;
    });

    pdf.save(`customer_${customer.id}_report.pdf`);
  };

  // Helper function — add only page number at bottom center
  const addPageNumber = (
    pdf: jsPDF,
    pageNumber: number,
    totalPages: number,
    pdfWidth: number,
    pdfHeight: number
  ) => {
    pdf.setFontSize(10);
    pdf.setTextColor(80);
    pdf.text(
      `Page ${pageNumber} of ${totalPages}`,
      pdfWidth / 2,
      pdfHeight - 5, // bottom center
      { align: "center" }
    );
  };

  const handleDownloadAllPDFs = async () => {
    for (const id of selectedRowIds) {
      const customerId = typeof id === "number" ? id : parseInt(id, 10);
      const customer = customerListReport.find((c) => c.id === customerId);
      if (!customer) continue;

      await generatePDFForCustomer(customer);
    }
  };

  return (
    <>
      <Button
        className={classes.addNewButton}
        startIcon={<FileDownloadIcon />}
        onClick={handleDownloadAllPDFs}
        disabled={selectedRowIds.length === 0}
      >
        Download PDFs
      </Button>

      {selectedRowIds.map((id) => {
        const customerId = typeof id === "number" ? id : parseInt(id, 10);
        const customer = customerListReport.find((c) => c.id === customerId);
        if (!customer) return null;

        return (
          <div
            key={customer.id}
            id={`pdf-customer-${customer.id}`}
            style={{
              width: "800px",
              padding: "20px",
              background: "#fff",
              position: "absolute",
              left: "-9999px",
            }}
          >
            <Grid container spacing={"2px"} padding={"1px"}>
              <Grid
                item
                lg={12}
                md={12}
                sm={12}
                xs={12}
                className={classes.titleGrid}
              >
                <Grid
                  container
                  className={classes.titleGridInvoice}
                  justifyContent={"space-between"}
                  padding={1}
                >
                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <Grid container spacing={"1px"}>
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Grid
                          container
                          spacing={1}
                          justifyContent="space-between"
                          alignItems="flex-start"
                        >
                          {/* Left Side: Logo */}
                          <Grid item>
                            <img
                              src={LogoMain}
                              alt="AttoDesk Logo"
                              style={{
                                width: "300px",
                                height: "auto",
                                display: "block",
                              }}
                            />
                          </Grid>

                          {/* Right Side: Contact Info stacked vertically */}
                          <Grid item>
                            <Grid container direction="column" spacing={"1px"}>
                              {customer &&
                                (customer.customerFirstName ||
                                  customer.customerLastName) && (
                                  <Grid item textAlign={"right"}>
                                    <Typography className={classes.bodyPrint1}>
                                      {/* <span style={{ fontWeight: fontwhighte }}>
                                        Name:&nbsp;
                                      </span> */}
                                      {(customer?.customerFirstName || "") +
                                        " " +
                                        (customer?.customerLastName || "")}
                                    </Typography>
                                  </Grid>
                                )}
                              {customer && customer.customerHomeMobileNum && (
                                <Grid item textAlign={"right"}>
                                  <Typography className={classes.bodyPrint1}>
                                    <span style={{ fontWeight: fontwhighte }}>
                                      Home Phone:&nbsp;
                                    </span>
                                    {formatPhoneNumberForDisplay(
                                      customer.customerHomeMobileNum
                                    )}
                                  </Typography>
                                </Grid>
                              )}

                              {customer && customer.customerMobileNum && (
                                <Grid item textAlign={"right"}>
                                  <Typography className={classes.bodyPrint1}>
                                    <span style={{ fontWeight: fontwhighte }}>
                                      Cell Phone:&nbsp;
                                    </span>
                                    {formatPhoneNumberForDisplay(
                                      customer.customerMobileNum
                                    )}
                                  </Typography>
                                </Grid>
                              )}

                              {customer && customer.customerWorkMobileNum && (
                                <Grid item textAlign={"right"}>
                                  <Typography className={classes.bodyPrint1}>
                                    <span style={{ fontWeight: fontwhighte }}>
                                      Work Phone:&nbsp;
                                    </span>
                                    {formatPhoneNumberForDisplay(
                                      customer.customerWorkMobileNum
                                    )}
                                  </Typography>
                                </Grid>
                              )}

                              {customer && customer.customerEmail && (
                                <Grid item textAlign={"right"}>
                                  <Typography className={classes.bodyPrint1}>
                                    <span style={{ fontWeight: fontwhighte }}>
                                      Email:&nbsp;
                                    </span>
                                    {customer.customerEmail.toLowerCase()}
                                  </Typography>
                                </Grid>
                              )}
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid
                        item
                        lg={12}
                        md={12}
                        sm={12}
                        xs={12}
                        // className={classes.policySummaryBox}
                        className={classes.titleGrid}
                        textAlign={"center"}
                        justifyContent={"center"}
                        // sx={{ borderBottom: 1, mt: 2 }}
                        sx={{ mt: 2 }}
                      >
                        {/* <Typography className={classes.policySummaryTitile}>
                            Policy Summary
                          </Typography> */}
                      </Grid>

                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Typography className={classes.bodyPrint1}>
                          {/* <span style={{ fontWeight: fontwhighte }}> */}
                          Date :&nbsp;&nbsp;
                          {/* </span>{" "} */}
                          {formattedDate}
                        </Typography>
                      </Grid>
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        {customer &&
                          (customer.customerFirstName ||
                            customer.customerLastName) && (
                            <Typography className={classes.bodyPrint1}>
                              {(customer?.customerFirstName || "") +
                                " " +
                                (customer?.customerLastName || "")}
                            </Typography>
                          )}
                        {customer &&
                          (customer.customerAddresLine ||
                            customer.customerCity) && (
                            <Typography
                              className={classes.bodyPrint1}
                              // sx={{
                              //   textTransform: "uppercase",
                              //   color: "black",
                              // }}
                            >
                              {toProperCase(
                                customer?.customerAddresLine || ""
                              ) +
                                " " +
                                toProperCase(customer?.customerCity || "")}
                            </Typography>
                          )}
                        {customer &&
                          (customer.customerProvince ||
                            customer.customerPostalCode) && (
                            <Typography
                              className={classes.bodyPrint1}
                              // sx={{
                              //   textTransform: "uppercase",
                              //   // color: "black",
                              // }}
                            >
                              {toAllCaps(customer?.customerProvince || "") +
                                " " +
                                toAllCaps(customer?.customerPostalCode || "")}
                            </Typography>
                          )}
                      </Grid>
                      {initialData && initialData.header_message && (
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <Typography
                            className={classes.bodyPrint133}
                            component="div"
                            sx={{
                              lineHeight: 0.2,
                            }}
                            dangerouslySetInnerHTML={{
                              __html: initialData.header_message,
                            }}
                          />
                        </Grid>
                      )}
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        {(customer?.policySummaryIds || []).map(
                          (policyNumber: string, index: number) => {
                            const policies =
                              customer?.groupedPolicies as Record<
                                string,
                                IPolicy[]
                              >;
                            const records = policies?.[policyNumber] || [];
                            const firstPolicy = records[0];
                            return (
                              <div key={index}>
                                <Grid container spacing={1}>
                                  <Grid
                                    item
                                    lg={12}
                                    md={12}
                                    sm={12}
                                    xs={12}
                                    mt={1}
                                    className={classes.titleGrid}
                                  >
                                    <Grid
                                      container
                                      spacing={1}
                                      key={policyNumber}
                                    >
                                      <Grid
                                        item
                                        lg={12}
                                        md={12}
                                        sm={12}
                                        xs={12}
                                      >
                                        <TableContainer>
                                          <Table
                                            className={classes.tableReport}
                                          >
                                            <TableHead
                                              className={
                                                classes.tableHeaderRowReport
                                              }
                                            >
                                              <TableRow>
                                                <TableCell
                                                  className={
                                                    classes.tablheaderReport
                                                  }
                                                  colSpan={3}
                                                  sx={{
                                                    width: "100%",
                                                  }}
                                                >
                                                  <Grid
                                                    container
                                                    spacing={"1px"}
                                                  >
                                                    <Grid
                                                      item
                                                      lg={6}
                                                      md={6}
                                                      sm={6}
                                                      xs={6}
                                                    >
                                                      {firstPolicy.policyNumber && (
                                                        <Typography
                                                          className={
                                                            classes.bodyPrint1
                                                          }
                                                        >
                                                          <span
                                                            style={{
                                                              fontWeight:
                                                                fontwhighte,
                                                            }}
                                                          >
                                                            Policy Number
                                                            :&nbsp;&nbsp;
                                                          </span>
                                                          {firstPolicy.policyNumber ||
                                                            " "}
                                                        </Typography>
                                                      )}
                                                      {firstPolicy.policyInsuranceCompanyId && (
                                                        <Typography
                                                          className={
                                                            classes.bodyPrint1
                                                          }
                                                        >
                                                          <span
                                                            style={{
                                                              fontWeight:
                                                                fontwhighte,
                                                            }}
                                                          >
                                                            Company Name
                                                            :&nbsp;&nbsp;
                                                          </span>
                                                          {brandMap.get(
                                                            Number(
                                                              firstPolicy.policyInsuranceCompanyId
                                                            )
                                                          ) || ""}
                                                        </Typography>
                                                      )}
                                                      {firstPolicy.policyIssueDate && (
                                                        <Typography
                                                          className={
                                                            classes.bodyPrint1
                                                          }
                                                        >
                                                          <span
                                                            style={{
                                                              fontWeight:
                                                                fontwhighte,
                                                            }}
                                                          >
                                                            Issue Date
                                                            :&nbsp;&nbsp;
                                                          </span>
                                                          {dayjs(
                                                            firstPolicy.policyIssueDate
                                                          ).format(
                                                            "MMM D, YYYY"
                                                          )}
                                                        </Typography>
                                                      )}
                                                    </Grid>

                                                    <Grid
                                                      item
                                                      lg={6}
                                                      md={6}
                                                      sm={6}
                                                      xs={6}
                                                    >
                                                      {firstPolicy.policyPayor && (
                                                        <Typography
                                                          className={
                                                            classes.bodyPrint1
                                                          }
                                                        >
                                                          <span
                                                            style={{
                                                              fontWeight:
                                                                fontwhighte,
                                                            }}
                                                          >
                                                            Payor Name
                                                            :&nbsp;&nbsp;
                                                          </span>
                                                          {firstPolicy.policyPayor ||
                                                            " "}
                                                        </Typography>
                                                      )}
                                                      {firstPolicy.policyDrawDay && (
                                                        <Typography
                                                          className={
                                                            classes.bodyPrint1
                                                          }
                                                        >
                                                          <span
                                                            style={{
                                                              fontWeight:
                                                                fontwhighte,
                                                            }}
                                                          >
                                                            Draw Day
                                                            :&nbsp;&nbsp;
                                                          </span>
                                                          {firstPolicy.policyDrawDay ||
                                                            " "}
                                                        </Typography>
                                                      )}
                                                    </Grid>
                                                  </Grid>
                                                </TableCell>
                                              </TableRow>
                                              <TableRow>
                                                <TableCell
                                                  className={
                                                    classes.tablheaderReport
                                                  }
                                                  sx={{
                                                    // textAlign: "center",
                                                    width: "50%",
                                                    // padding: "none"
                                                  }}
                                                >
                                                  <Typography
                                                    className={
                                                      classes.bodyPrint1333
                                                    }
                                                  >
                                                    <strong>Details</strong>
                                                  </Typography>
                                                </TableCell>
                                                <TableCell
                                                  className={
                                                    classes.tablheaderReport
                                                  }
                                                  sx={{
                                                    textAlign: "right",
                                                    width: "30%",
                                                    // padding: "none"
                                                  }}
                                                >
                                                  <Typography
                                                    className={
                                                      classes.bodyPrint1333
                                                    }
                                                  >
                                                    <strong>
                                                      Coverage Amount{" "}
                                                    </strong>
                                                  </Typography>
                                                </TableCell>
                                                <TableCell
                                                  className={
                                                    classes.tablheaderReport
                                                  }
                                                  sx={{
                                                    textAlign: "right",
                                                    width: "20%",
                                                    // padding: "none"
                                                  }}
                                                >
                                                  <Typography
                                                    className={
                                                      classes.bodyPrint1333
                                                    }
                                                  >
                                                    <strong>Premium</strong>
                                                  </Typography>
                                                </TableCell>
                                              </TableRow>
                                            </TableHead>
                                            <TableBody>
                                              {cutomerProjectisError ||
                                              records.length === 0 ? (
                                                <TableRow>
                                                  <TableCell
                                                    colSpan={8}
                                                    align="center"
                                                  >
                                                    {cutomerProjectisError
                                                      ? "Error fetching data"
                                                      : "No data available"}
                                                  </TableCell>
                                                </TableRow>
                                              ) : (
                                                records.map(
                                                  (
                                                    policy: IPolicy,
                                                    index: number
                                                  ) => (
                                                    <TableRow
                                                      className={
                                                        classes.tableRowReport
                                                      }
                                                      key={policy.id}
                                                    >
                                                      <TableCell
                                                        className={
                                                          classes.tablCellReport
                                                        }
                                                        sx={{
                                                          width: "50%",
                                                        }}
                                                      >
                                                        {policy.policyProductId && (
                                                          <Typography
                                                            className={
                                                              classes.bodyPrintHeading
                                                            }
                                                            sx={{
                                                              textTransform:
                                                                "uppercase",
                                                              color: "black",
                                                              fontWeight:
                                                                "400 !important",
                                                            }}
                                                          >
                                                            {productMap.get(
                                                              Number(
                                                                policy.policyProductId
                                                              )
                                                            ) || ""}
                                                          </Typography>
                                                        )}
                                                        {policy.policyInsuredDetails &&
                                                          policy
                                                            .policyInsuredDetails
                                                            .length > 0 && (
                                                            <Typography
                                                              className={
                                                                classes.bodyPrint1
                                                              }
                                                            >
                                                              <span
                                                                style={{
                                                                  fontWeight:
                                                                    fontwhighte,
                                                                }}
                                                              >
                                                                Insured
                                                                :&nbsp;&nbsp;
                                                              </span>{" "}
                                                              <strong> </strong>{" "}
                                                              {policy.policyInsuredDetails.map(
                                                                (
                                                                  insuredId,
                                                                  index
                                                                ) => {
                                                                  const customerName =
                                                                    relativeMap.get(
                                                                      Number(
                                                                        insuredId
                                                                      )
                                                                    ) || "";

                                                                  return (
                                                                    <span
                                                                      key={
                                                                        insuredId
                                                                      }
                                                                    >
                                                                      {
                                                                        customerName
                                                                      }
                                                                      {index <
                                                                        policy
                                                                          .policyInsuredDetails
                                                                          .length -
                                                                          1 &&
                                                                        ", "}
                                                                    </span>
                                                                  );
                                                                }
                                                              )}
                                                            </Typography>
                                                          )}
                                                        {(policy.policyIsLifeTimeExpiryDate ||
                                                          policy.policyExpiryDate) && (
                                                          <Typography
                                                            className={
                                                              classes.bodyPrint1
                                                            }
                                                          >
                                                            {/* Expires :&nbsp;&nbsp; */}
                                                            <span
                                                              style={{
                                                                fontWeight:
                                                                  fontwhighte,
                                                              }}
                                                            >
                                                              Expires
                                                              :&nbsp;&nbsp;
                                                            </span>{" "}
                                                            {policy.policyIsLifeTimeExpiryDate
                                                              ? "Death"
                                                              : policy.policyExpiryDate
                                                              ? dayjs(
                                                                  policy.policyExpiryDate
                                                                ).format(
                                                                  "MMM D, YYYY"
                                                                )
                                                              : "-"}
                                                          </Typography>
                                                        )}

                                                        {policy.policyPrimaryBenificariesNew &&
                                                          policy
                                                            .policyPrimaryBenificariesNew
                                                            .length > 0 && (
                                                            <Typography
                                                              className={
                                                                classes.bodyPrint1
                                                              }
                                                            >
                                                              {/* Beneficiaries :&nbsp;&nbsp; */}
                                                              <span
                                                                style={{
                                                                  fontWeight:
                                                                    fontwhighte,
                                                                }}
                                                              >
                                                                Beneficiaries
                                                                :&nbsp;&nbsp;
                                                              </span>{" "}
                                                              {/* {policy.policyPrimaryBenificariesNew.map(
                                                              (
                                                                insured,
                                                                index
                                                              ) => (
                                                                <span
                                                                  key={index}
                                                                >
                                                                  {toProperCase(
                                                                    insured.name
                                                                  )}{" "}
                                                                  {insured.payoutPercentage
                                                                    ? `- ${insured.payoutPercentage}%`
                                                                    : ""}
                                                                  {index <
                                                                    policy
                                                                      .policyPrimaryBenificariesNew
                                                                      .length -
                                                                      1 && ", "}
                                                                </span>
                                                              )
                                                            )} */}
                                                              {policy.policyPrimaryBenificariesNew.map(
                                                                (
                                                                  insuredId,
                                                                  index
                                                                ) => {
                                                                  const customerName =
                                                                    relativeMap.get(
                                                                      Number(
                                                                        insuredId.column1
                                                                      )
                                                                    ) || "";

                                                                  return (
                                                                    <span
                                                                      key={
                                                                        index
                                                                      }
                                                                    >
                                                                      {
                                                                        customerName
                                                                      }
                                                                      {insuredId.payoutPercentage
                                                                        ? `- ${insuredId.payoutPercentage}%`
                                                                        : ""}
                                                                      {/* {insuredId.column2
                                                              ? ` (${insuredId.column2})`
                                                              : ""} */}
                                                                      {index <
                                                                        policy
                                                                          .policyPrimaryBenificariesNew
                                                                          .length -
                                                                          1 &&
                                                                        ", "}
                                                                    </span>
                                                                  );
                                                                }
                                                              )}
                                                            </Typography>
                                                          )}
                                                        {firstPolicy.policyRemark && (
                                                          <Typography
                                                            className={
                                                              classes.bodyPrint1
                                                            }
                                                          >
                                                            <span
                                                              style={{
                                                                fontWeight:
                                                                  fontwhighte,
                                                              }}
                                                            >
                                                              Remarks:
                                                            </span>{" "}
                                                            <Typography
                                                              className={
                                                                classes.bodyPrint1
                                                              }
                                                              sx={{
                                                                lineHeight: 0.4,
                                                              }}
                                                              dangerouslySetInnerHTML={{
                                                                __html:
                                                                  policy.policyRemark,
                                                              }}
                                                            />
                                                            {/* {policy.policyRemark ||
                                                                  ""} */}
                                                          </Typography>
                                                        )}
                                                      </TableCell>
                                                      <TableCell
                                                        className={
                                                          classes.tablCellReport
                                                        }
                                                        sx={{
                                                          textAlign: "right",
                                                          width: "20%",
                                                          verticalAlign: "top",
                                                        }}
                                                      >
                                                        {policy.policyCoverageAmount && (
                                                          <Typography
                                                            className={
                                                              classes.bodyPrintHeading
                                                            }
                                                            sx={{
                                                              textTransform:
                                                                "uppercase",
                                                              color: "black",
                                                            }}
                                                          >
                                                            {formatNumber(
                                                              policy.policyCoverageAmount ||
                                                                "0.00"
                                                            )}
                                                          </Typography>
                                                        )}
                                                      </TableCell>
                                                      <TableCell
                                                        className={
                                                          classes.tablCellReport
                                                        }
                                                        sx={{
                                                          textAlign: "right",
                                                          width: "30%",
                                                          verticalAlign: "top",
                                                        }}
                                                      >
                                                        {policy.policyPremiumDetails && (
                                                          <Typography
                                                            className={
                                                              classes.bodyPrintHeading
                                                            }
                                                            sx={{
                                                              textTransform:
                                                                "uppercase",
                                                              color: "black",
                                                            }}
                                                          >
                                                            {formatNumber(
                                                              policy.policyPremiumDetails ||
                                                                "0.00"
                                                            )}
                                                          </Typography>
                                                        )}
                                                      </TableCell>
                                                    </TableRow>
                                                  )
                                                )
                                              )}
                                              <TableRow>
                                                <TableCell
                                                  className={
                                                    classes.tablCellReport
                                                  }
                                                  sx={{
                                                    width: "50%",
                                                  }}
                                                >
                                                  <Typography
                                                    className={
                                                      classes.bodyPrint1
                                                    }
                                                  >
                                                    <span
                                                      style={{
                                                        fontWeight: fontwhighte,
                                                      }}
                                                    >
                                                      Total Premium
                                                    </span>{" "}
                                                  </Typography>
                                                </TableCell>
                                                <TableCell
                                                  className={
                                                    classes.tablCellReport
                                                  }
                                                  sx={{
                                                    textAlign: "right",
                                                    width: "30%",
                                                    verticalAlign: "top",
                                                  }}
                                                >
                                                  <Typography
                                                    className={
                                                      classes.bodyPrintHeading
                                                    }
                                                    sx={{
                                                      textTransform:
                                                        "uppercase",
                                                      color: "black",
                                                    }}
                                                  >
                                                    {" "}
                                                  </Typography>
                                                </TableCell>
                                                <TableCell
                                                  className={
                                                    classes.tablCellReport
                                                  }
                                                  sx={{
                                                    textAlign: "right",
                                                    width: "20%",
                                                    verticalAlign: "top",
                                                  }}
                                                >
                                                  <Typography
                                                    className={
                                                      classes.bodyPrintHeading
                                                    }
                                                    sx={{
                                                      textTransform:
                                                        "uppercase",
                                                      color: "black",
                                                    }}
                                                  >
                                                    <strong>
                                                      {formatNumber(
                                                        firstPolicy.policyPremium ||
                                                          "0.00"
                                                      )}
                                                    </strong>
                                                  </Typography>
                                                </TableCell>
                                              </TableRow>
                                            </TableBody>
                                          </Table>
                                        </TableContainer>
                                      </Grid>
                                    </Grid>
                                  </Grid>
                                </Grid>
                              </div>
                            );
                          }
                        )}
                      </Grid>

                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        {initialData && initialData.body_message_start && (
                          <Typography
                            className={classes.bodyPrint1}
                            component="div"
                            sx={{
                              lineHeight: 0.4,
                              margin: 0,
                            }}
                            dangerouslySetInnerHTML={{
                              __html: initialData.body_message_start,
                            }}
                          />
                        )}
                        {customer.policyTermsArray &&
                          customer.policyTermsArray.length > 0 && (
                            <List
                              sx={{
                                listStyleType: "disc",
                                m: 0,
                                p: 0,
                                pl: 4,
                                "& .MuiListItem-root": {
                                  py: 0,
                                  my: 0,
                                  mb: 0,
                                  m: 0,

                                  lineHeight: 0.1,
                                  "@media print": {
                                    lineHeight: 0.1,
                                    my: 0,
                                    mb: 0,
                                    m: 0,
                                    py: 0,
                                  },
                                },
                              }}
                            >
                              {customer.policyTermsArray &&
                                customer.policyTermsArray.length > 0 &&
                                customer.policyTermsArray.map((policyGroup) => {
                                  const productName =
                                    productMap.get(
                                      Number(policyGroup.policyTermProductId)
                                    ) || "";

                                  return (
                                    <Grid
                                      key={policyGroup.policyTermProductId}
                                      container
                                    >
                                      {/* <Grid item lg={12} md={12} sm={12} xs={12}>
                                    <Typography
                                      className={classes.bodyPrint1}
                                      pt={2}
                                    >
                                      <strong>{productName}</strong>
                                    </Typography>
                                  </Grid> */}
                                      {policyGroup.policyTermsDescriptionArray.map(
                                        (term) => (
                                          <ListItem
                                            key={term.id}
                                            sx={{
                                              display: "list-item",
                                              "@media print": {
                                                pageBreakInside: "avoid",
                                              },
                                              lineHeight: 0.1,
                                              paddingTop: 0,
                                              paddingBottom: 0,
                                              m: 0,
                                              p: 0,
                                            }}
                                          >
                                            <ListItemText
                                              primary={
                                                <Typography
                                                  className={
                                                    classes.bodyPrint133
                                                  }
                                                  component="div"
                                                  sx={{ lineHeight: 1.1 }}
                                                  dangerouslySetInnerHTML={{
                                                    __html:
                                                      term.policyTermsDescription,
                                                  }}
                                                />
                                              }
                                            />
                                          </ListItem>
                                        )
                                      )}
                                    </Grid>
                                  );
                                })}
                            </List>
                          )}

                        {initialData && initialData.body_message_end && (
                          <Typography
                            className={classes.bodyPrint133}
                            component="div"
                            sx={{
                              lineHeight: 0.4,
                            }}
                            dangerouslySetInnerHTML={{
                              __html: initialData.body_message_end,
                            }}
                          />
                        )}

                        {initialData && initialData.footer_message && (
                          <Grid sx={{ mt: 3 }}>
                            <Typography
                              className={classes.bodyPrint133}
                              component="div"
                              sx={{
                                lineHeight: 0.2,
                              }}
                              dangerouslySetInnerHTML={{
                                __html: initialData.footer_message,
                              }}
                            />
                          </Grid>
                        )}
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </div>
        );
      })}
    </>
  );
};

export default MultiPDFDownload;
